import { FC, useState } from "react";
import { Tabs, Breadcrumb } from "flowbite-react";
import { HiChartBar, HiChartPie, HiHome, HiLightBulb, HiCurrencyDollar } from "react-icons/hi";
import NavbarSidebarLayout from "../../layouts/navbar-sidebar";
import ClientConversationAnalysis from "./ClientConversationAnalysis";
import ChannelDistributionReport from "./ChannelDistributionReport";
import AgentHistoricalOccupancyReport from "./AgentHistoricalOccupancyReport";
import TicketAnalysisReport from "./TicketAnalysisReport";
import KBIssueInsightsReport from "./KBIssueInsightsReport";
import AiAgentCostReport from "./AiAgentCostReport";
import { useTranslation } from "react-i18next";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { agentRoles } from "@/constants/agent";
import SuperAdminCompanyBar from "@/components/SuperAdminCompanyBar";
import SelectCompanyPrompt from "@/components/SelectCompanyPrompt";
import { useSelectedCompany } from "@/context/SelectedCompanyContext";

const ReportsPage: FC = function () {
  const { t } = useTranslation(["reports"]);
  const user = useCurrentUser();
  const isSuperAdmin = user?.agent_role === agentRoles.super_admin;
  const { selectedCompany } = useSelectedCompany();
  const needsCompany = isSuperAdmin && !selectedCompany;

  const tabs = [
    {
      title: t("analysisTitle"),
      icon: HiChartBar,
      content: <ClientConversationAnalysis />,
    },
    {
      title: t("channelDistributionTitle"),
      icon: HiChartPie,
      content: <ChannelDistributionReport />,
    },
    ...(isSuperAdmin
      ? []
      : [
          {
            title: t("agentOccupancyTitle"),
            icon: HiChartBar,
            content: <AgentHistoricalOccupancyReport />,
          },
          {
            title: t("ticketAnalysisTitle"),
            icon: HiChartPie,
            content: <TicketAnalysisReport />,
          },
        ]),
    {
      title: t("kbIssueInsights.title"),
      icon: HiLightBulb,
      content: <KBIssueInsightsReport />,
    },
    {
      title: t("aiCost.title"),
      icon: HiCurrencyDollar,
      content: <AiAgentCostReport />,
    },
  ];

  const [activeTab, setActiveTab] = useState<number>(() => {
    const saved = localStorage.getItem("reportsActiveTab");
    const parsed = saved ? parseInt(saved, 10) : 0;
    // Clamp to a valid index — the saved tab may no longer exist (e.g. a
    // company admin whose super-admin tab is now hidden).
    return Number.isNaN(parsed) ? 0 : Math.min(Math.max(parsed, 0), tabs.length - 1);
  });

  const handleTabChange = (index: number) => {
    setActiveTab(index);
    localStorage.setItem("reportsActiveTab", index.toString());
  };

  const safeActiveTab = Math.min(activeTab, tabs.length - 1);

  return (
    <NavbarSidebarLayout>
      <div className="px-4 pt-6">
        <Breadcrumb className="mb-4">
          <Breadcrumb.Item href="/">
            <div className="flex items-center gap-x-3">
              <HiHome className="text-xl" />
              <span className="dark:text-white">{t("home")}</span>
            </div>
          </Breadcrumb.Item>
          <Breadcrumb.Item>{t("reports")}</Breadcrumb.Item>
        </Breadcrumb>

        <SuperAdminCompanyBar />

        {needsCompany ? (
          <SelectCompanyPrompt />
        ) : (
        <Tabs
          aria-label="Report tabs"
          variant="pills"
          onActiveTabChange={handleTabChange}
          theme={{
            tablist: {
              base: "flex text-center justify-center",
              tabitem: {
                variant: {
                  pills: {
                    active: {
                      on: "rounded-full bg-gray-300 dark:bg-blue-900 text-black dark:text-white",
                      off: "rounded-full hover:bg-gray-100 hover:text-gray-900 dark:hover:bg-gray-800 dark:hover:text-white",
                    },
                  },
                },
              },
            }
          }}
        >
            {tabs.map((tab, index) => (
              <Tabs.Item
                key={index}
                active={safeActiveTab === index}
                title={tab.title}
                icon={tab.icon}
              >
                {tab.content}
              </Tabs.Item>
            ))}
        </Tabs>
        )}
      </div>
    </NavbarSidebarLayout>
  );
};

export default ReportsPage;
